/**
 * Structured Table Visualizer Tool
 *
 * Renders JSON, YAML, and TOML as hierarchical nested tables with
 * collapsible sub-tables for nested objects and arrays.
 */

import React from 'react';
import { parse as parseTOML } from 'smol-toml';
import YAML from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

import { StructuredTableView } from './StructuredTableView';

/** Attempt to parse input as JSON, YAML, or TOML. */
function parseStructuredInput(input: string): unknown {
  // Try JSON first (most common)
  try {
    return parseJsonLenient(input);
  } catch {
    // not JSON
  }

  // Try TOML (check before YAML since most YAML parsers accept TOML-like strings)
  try {
    const result = parseTOML(input);
    if (result && typeof result === 'object') return result;
  } catch {
    // not TOML
  }

  // Try YAML
  try {
    const result = YAML.parse(input);
    if (result !== undefined && result !== null) return result;
  } catch {
    // not YAML
  }

  throw ToolError.validation('Unable to parse input as JSON, YAML, or TOML');
}

/**
 * Structured Table Visualizer - renders structured data as nested tables.
 */
class StructuredTableVisualizerToolImpl implements IVisualizerTool {
  readonly id = 'structured-table-visualizer';
  readonly name = 'Structured Table';
  readonly description = 'Display JSON, YAML, or TOML as hierarchical nested tables';
  readonly version = '1.0.0';
  readonly type = 'visualizer' as const;
  readonly category = 'Visualizers';
  readonly editable = false;
  readonly keywords = ['json', 'yaml', 'toml', 'table', 'structured', 'nested', 'hierarchy', 'visualizer'];

  readonly supportedInputTypes: ISemanticType[] = [
    SEMANTIC_TYPES.JSON,
    SEMANTIC_TYPES.YAML,
    SEMANTIC_TYPES.TOML,
  ];

  readonly outputType = undefined;

  supportsInput(): boolean {
    return true;
  }

  readonly attributes = [
    {
      name: 'maxDepth',
      type: 'number' as const,
      label: 'Max Depth',
      defaultValue: 3,
      description: 'Maximum nesting depth for sub-tables',
      validation: { min: 1, max: 10 },
    },
    {
      name: 'expandedByDefault',
      type: 'boolean' as const,
      label: 'Expanded By Default',
      defaultValue: false,
      description: 'Whether nested sub-tables are expanded by default',
    },
  ];

  render(context: VisualizerRenderContext): React.ReactElement {
    const { input, config } = context;

    const effectiveInput = input.trim() || '{}';

    const parseResult = React.useMemo<{ data: unknown } | { error: string }>(() => {
      try {
        return { data: parseStructuredInput(effectiveInput) };
      } catch (e) {
        return { error: e instanceof Error ? e.message : 'Unable to parse input' };
      }
    }, [effectiveInput]);

    if ('error' in parseResult) {
      return (
        <div className="p-4 text-red-500">
          <div className="font-semibold">Parse Error</div>
          <div className="text-sm mt-1">{parseResult.error}</div>
          <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-32">
            {input.substring(0, 200)}...
          </pre>
        </div>
      );
    }

    return (
      <StructuredTableView
        data={effectiveInput}
        maxDepth={(config.maxDepth as number) ?? 3}
        expandedByDefault={(config.expandedByDefault as boolean) ?? false}
      />
    );
  }

  /**
   * Pass-through per V4 invariant.
   */
  toText(input: string): string {
    return input;
  }
}

export const StructuredTableVisualizerTool = new StructuredTableVisualizerToolImpl();
